import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { ArticleHeader } from '@components/ArticleHeader';
import { articleStyles as classes } from './styleObjects/articleStyles';
import { BottomArticleCardList } from '@components/BottomArticleCardList';
import { useAtom } from 'jotai';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { useTranslation } from '@stateManagement/TranslationContext';
import { ClientProfile } from '@components/SuccessStories/ClientProfile';
import { SuccessStoryArticleCard } from '@components/SuccessStories/SuccessStoryArticleCard';
import { CallToActionBanner } from '@components/SectionComponents';
import { Redirect } from '@components/Redirect';
import { ArticleSectionPageHelmet } from '@components/BlogSection/ArticleSectionPageHelmet';
import { getTranslation } from '@helpers/languageTranslations';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { cleanEscapes } from '@helpers/string';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { getSuccessStoryStructuredDataScript } from '@helpers/articleScriptHelper';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { Heading } from '@web-for-marketing/react-ui';
import BannerImage from '@images/SuccessStories/optimize-your-fleet-cta-bg-case-study-template-mobile@2x.jpg';

export default function SuccessStory(): JSX.Element | null {
    const { selectedLanguageTranslation } = useTranslation();
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const { prefixStringWithBaseUrl, mediaUrlPrefix, successStories, fullUrl } = useStaticValues();
    const cleanFullUrl = cleanEscapes(fullUrl, '"');
    const successStoriesTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'successStoriesTemplate'),
        [selectedLanguageTranslation]
    );
    const successStoriesArticle = successStories.articleData;
    const hasHeadermedia =
        successStoriesArticle?.successStory.imageUrl || successStoriesArticle?.successStory.mobileImageUrl;

    return successStoriesArticle ? (
        successStoriesArticle.successStory.redirectUrl ? (
            <Redirect statusCode={200} redirectUrl={successStoriesArticle.successStory.redirectUrl} />
        ) : (
            <>
                <ArticleSectionPageHelmet
                    article={successStories}
                    articleType='successStory'
                    noIndex={successStoriesArticle.successStory.noIndex}
                    script={getSuccessStoryStructuredDataScript(
                        successStoriesArticle.successStory,
                        mediaUrlPrefix,
                        prefixStringWithBaseUrl,
                        cleanFullUrl
                    )}
                />
                <Grid container direction='column'>
                    <Grid item css={classes.header}>
                        <ArticleHeader article={successStoriesArticle.successStory} />
                    </Grid>
                    <Grid>
                        <Grid
                            container
                            spacing={6}
                            css={[
                                { margin: '-24px', '& > .MuiGrid-item': { padding: '24px' } },
                                classes.centeredContainer,
                                classes.mobileWrap,
                            ]}
                            wrap='nowrap'
                        >
                            <Grid
                                item
                                md={12}
                                lg={8}
                                css={hasHeadermedia ? classes.pressReleaseCard : classes.noHeaderMediaCard}
                            >
                                <SuccessStoryArticleCard
                                    successStory={successStoriesArticle.successStory}
                                    successStoriesTranslation={successStoriesTranslation}
                                />
                            </Grid>
                            <Grid item md={12} lg={4} css={classes.clientProfile}>
                                <Heading
                                    variant='h3'
                                    weight='regular'
                                    component='p'
                                    color='primary'
                                    css={classes.clientProfileHeader}
                                >
                                    {successStoriesTranslation.clientProfile}
                                </Heading>
                                <ClientProfile
                                    clientProfile={successStoriesArticle.successStory.clientProfile}
                                    successStoriesTranslation={successStoriesTranslation}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container css={[classes.centeredContainer, classes.block]}>
                            <Grid css={classes.recentNewsContainer}>
                                <Heading variant='h2' color='primary'>
                                    {successStoriesTranslation.otherStories}
                                </Heading>
                                <BottomArticleCardList
                                    articleList={successStoriesArticle.otherStories}
                                    routeType={languageRouteTypes.CASE_STUDY}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <CallToActionBanner
                        lazyLoad
                        imagePath={prefixStringWithBaseUrl(BannerImage)}
                        title={successStoriesTranslation.optimizeYourFleet}
                        description={successStoriesTranslation.letUsShowYou}
                        align='center'
                        buttons={[
                            {
                                buttonText: successStoriesTranslation.requestDemo,
                                buttonLinkPath: successStoriesTranslation.requestDemoPath,
                                buttonType: 'contained',
                                buttonTarget: false,
                            },
                            {
                                buttonText: successStoriesTranslation.visitBlog,
                                buttonLinkPath: getLanguageRouteAndPrefixByCodeAndRouteKey(
                                    selectedLanguage,
                                    languageRouteTypes.BLOG_HOME
                                ),
                                buttonType: 'outlined',
                                buttonTarget: false,
                            },
                        ]}
                    />
                </Grid>
            </>
        )
    ) : null;
}
